import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listReservations,
  ReservationListItem,
  ReservationStatus,
} from "../../../lib/adminApi";
import { useToast } from "../../../components/Toast";
import { czShortDate } from "../../../lib/dates";

const FILTERS: { value: ReservationStatus | "ALL"; label: string }[] = [
  { value: "PENDING", label: "Čekající" },
  { value: "CONFIRMED", label: "Potvrzené" },
  { value: "COMPLETED", label: "Proběhlé" },
  { value: "CANCELLED", label: "Zrušené" },
  { value: "ALL", label: "Všechny" },
];

export default function ReservationsList() {
  const toast = useToast();
  const [filter, setFilter] = useState<ReservationStatus | "ALL">("PENDING");
  const [items, setItems] = useState<ReservationListItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    listReservations({ status: filter })
      .then((r) => {
        setItems(r.reservations);
        setCounts(r.counts);
      })
      .catch((e) => toast.show("error", e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900">Rezervace</h1>
      <p className="mt-1 text-slate-600">
        Čekající rezervace schval nebo zamítni. Potvrzené se ukážou v kalendáři.
      </p>

      {/* Taby s počty */}
      <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {FILTERS.map((f) => {
          const count = f.value === "ALL"
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : counts[f.value] ?? 0;
          const active = filter === f.value;
          return (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                active
                  ? "bg-obloha-indigo-700 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {f.label}
              {count > 0 && (
                <span
                  className={`ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs ${
                    active ? "bg-white/20" : "bg-slate-200"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="mt-8 text-slate-500">Načítám…</div>
      ) : items.length === 0 ? (
        <div className="card mt-8 text-center text-slate-500">
          Žádné rezervace v tomto stavu.
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Termín</th>
                <th className="px-4 py-3 text-left">Školka</th>
                <th className="px-4 py-3 text-left">Kontakt</th>
                <th className="px-4 py-3 text-right">Děti</th>
                <th className="px-4 py-3 text-right">Km</th>
                <th className="px-4 py-3 text-right">Cena</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {items.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">
                      {czShortDate(new Date(r.date))}
                    </div>
                    <div className="text-xs text-slate-500">
                      {r.startTime}–{r.endTime}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.snapshotName}</div>
                    <div className="text-xs text-slate-500">{r.snapshotCity}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <div>{r.contactName}</div>
                    <div className="text-xs text-slate-500">{r.contactEmail}</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{r.childrenCount}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                    {r.distanceKm ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">
                    {r.totalPriceCzk.toLocaleString("cs-CZ")} Kč
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/reservations/${r.id}`}
                      className="text-sm font-medium text-obloha-indigo-700 hover:text-obloha-indigo-900"
                    >
                      Detail →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
